import freightFinancialApi from "./modules/freight-financial.api";
import {
  formatFinancialStatusValue,
  normalizeFinancialStatusValue,
  normalizeStoredFinancialStatusValue,
} from "../util/freight/freight-v2-table-utils";
import {
  buildFinancialInstallmentsForFreight,
  mergeInstallmentUpdate,
  resolveInstallmentBoletoAttachment,
} from "../util/freight/freight-financial-installments";

const BOLETO_MAX_SIZE_BYTES = 5 * 1024 * 1024;
const BOLETO_ALLOWED_EXTENSIONS = new Set(["pdf", "jpg", "jpeg", "png"]);
const DEFAULT_FINANCIAL_STATUS = "pendente";
const FINANCIAL_STATUS_INVALID_MESSAGE =
  "Status financeiro inválido. Use Pendente, Emitido, Pago, Vencido ou Cancelado.";
export const FINANCIAL_EDIT_LOCKED_MESSAGE =
  "Este registro financeiro não pode ser alterado porque seu status não está como Pendente.";

function getExtension(fileName) {
  return String(fileName || "")
    .split(".")
    .pop()
    ?.toLowerCase() || "";
}

function getFinancialStatusSource(record) {
  return (
    record?.statusFinancial ??
    record?.financialStatus ??
    (typeof record?.status === "string" ? record.status : null)
  );
}

export function isFinancialRecordEditable(status) {
  return normalizeFinancialStatusValue(status) === DEFAULT_FINANCIAL_STATUS;
}

export function validateBoletoFile(file) {
  if (!file) {
    return { valid: false, error: "Nenhum arquivo selecionado." };
  }

  const extension = getExtension(file.name);
  if (!BOLETO_ALLOWED_EXTENSIONS.has(extension)) {
    return {
      valid: false,
      error: "Formato inválido. Use PDF, JPG ou PNG.",
    };
  }

  if (file.size > BOLETO_MAX_SIZE_BYTES) {
    return {
      valid: false,
      error: "O arquivo deve ter no máximo 5MB.",
    };
  }

  return { valid: true };
}

function buildDefaultRecord(record) {
  const dueDateInstallments = Array.isArray(record?.dueDateInstallments)
    ? record.dueDateInstallments
    : [];
  const financialInstallments = buildFinancialInstallmentsForFreight(record);
  const installmentNumber = Number(record?.installmentNumber) || 1;
  const selectedInstallment =
    financialInstallments.find(
      (item) => item.installmentNumber === installmentNumber,
    ) || financialInstallments[0];
  const hasInstallments = financialInstallments.length > 1;
  const statusFinancial =
    normalizeStoredFinancialStatusValue(
      selectedInstallment?.statusFinancial ??
        getFinancialStatusSource(record),
      DEFAULT_FINANCIAL_STATUS,
    ) || DEFAULT_FINANCIAL_STATUS;

  return {
    freightId: record?.freightId || record?.id,
    installmentNumber: selectedInstallment?.installmentNumber ?? installmentNumber,
    installmentTotal: selectedInstallment?.installmentTotal ?? financialInstallments.length,
    installmentLabel: selectedInstallment?.installmentLabel,
    statusFinancial,
    financialStatus: statusFinancial,
    loadingDate: record?.firstDelivery?.dateColeta || null,
    dueDate:
      selectedInstallment?.dueDate ||
      record?.dueDate ||
      dueDateInstallments[0] ||
      null,
    dueDateInstallments,
    financialInstallments,
    value: Number(
      selectedInstallment?.value ?? record?.freight?.valueFreightage ?? 0,
    ),
    settlementDate:
      selectedInstallment?.settlementDate ??
      record?.settlementDate ??
      record?.dataBaixa ??
      null,
    dataBaixa:
      selectedInstallment?.dataBaixa ??
      record?.dataBaixa ??
      record?.settlementDate ??
      null,
    boletoAttachment: resolveInstallmentBoletoAttachment({
      financialInstallments,
      installmentNumber: selectedInstallment?.installmentNumber ?? installmentNumber,
      rootBoletoAttachment: record?.boletoAttachment ?? null,
    }),
    hasInstallments,
  };
}

function unwrapApiPayload(result) {
  const payload = result?.data ?? result ?? {};
  if (
    payload?.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }
  return payload;
}

function normalizeFinancialRecord(record, defaults = {}) {
  const merged = { ...defaults, ...(record || {}) };
  const installmentNumber = Number(defaults.installmentNumber);
  const installments = Array.isArray(merged.financialInstallments)
    ? merged.financialInstallments
    : defaults.financialInstallments || [];
  const selectedInstallment =
    Number.isFinite(installmentNumber) && installmentNumber > 0
      ? installments.find(
          (item) => Number(item.installmentNumber) === installmentNumber,
        )
      : null;

  const statusFinancial =
    normalizeStoredFinancialStatusValue(
      selectedInstallment?.statusFinancial ??
        getFinancialStatusSource(merged),
      DEFAULT_FINANCIAL_STATUS,
    ) || DEFAULT_FINANCIAL_STATUS;
  const settlementDate =
    selectedInstallment?.settlementDate ??
    selectedInstallment?.dataBaixa ??
    merged.settlementDate ??
    merged.dataBaixa ??
    defaults.settlementDate ??
    null;

  return {
    ...merged,
    freightId: merged.freightId || defaults.freightId,
    installmentNumber:
      selectedInstallment?.installmentNumber ?? defaults.installmentNumber,
    installmentTotal:
      selectedInstallment?.installmentTotal ?? defaults.installmentTotal,
    installmentLabel:
      selectedInstallment?.installmentLabel ?? defaults.installmentLabel,
    statusFinancial,
    financialStatus: statusFinancial,
    dueDate: selectedInstallment?.dueDate ?? defaults.dueDate ?? merged.dueDate,
    value: Number(
      selectedInstallment?.value ?? defaults.value ?? merged.value ?? 0,
    ),
    settlementDate,
    dataBaixa: settlementDate,
    financialInstallments: installments,
    boletoAttachment: resolveInstallmentBoletoAttachment({
      financialInstallments: installments,
      installmentNumber:
        selectedInstallment?.installmentNumber ?? defaults.installmentNumber,
      rootBoletoAttachment:
        merged.boletoAttachment ?? defaults.boletoAttachment ?? null,
    }),
  };
}

function buildSavePayload(payload = {}) {
  const statusFinancial =
    normalizeFinancialStatusValue(
      payload.statusFinancial ?? payload.financialStatus,
    );
  const settlementDate = payload.settlementDate ?? payload.dataBaixa ?? null;
  const installmentNumber = Number(payload.installmentNumber);

  if (!statusFinancial) {
    throw new Error(FINANCIAL_STATUS_INVALID_MESSAGE);
  }

  if (statusFinancial === "pago" && !settlementDate) {
    throw new Error("Informe a data de baixa.");
  }

  const basePayload = {
    ...payload,
    statusFinancial,
    financialStatus: formatFinancialStatusValue(statusFinancial),
    settlementDate,
    dataBaixa: payload.dataBaixa ?? settlementDate,
  };

  if (Number.isFinite(installmentNumber) && installmentNumber > 0) {
    const sourceInstallments =
      Array.isArray(payload.financialInstallments) &&
      payload.financialInstallments.length > 0
        ? payload.financialInstallments
        : buildFinancialInstallmentsForFreight(payload);

    const installmentPatch = {
      dueDate: payload.dueDate,
      value: payload.value,
      statusFinancial,
      financialStatus: formatFinancialStatusValue(statusFinancial),
      settlementDate: statusFinancial === "pago" ? settlementDate : null,
      dataBaixa: statusFinancial === "pago" ? settlementDate : null,
    };

    if (payload.boletoAttachment !== undefined) {
      installmentPatch.boletoAttachment = payload.boletoAttachment;
    }

    const installmentPayload = {
      ...basePayload,
      installmentNumber,
      installmentTotal: payload.installmentTotal,
      financialInstallments: mergeInstallmentUpdate(
        sourceInstallments,
        installmentNumber,
        installmentPatch,
      ),
    };

    return installmentPayload;
  }

  return basePayload;
}

function isNotFound(error) {
  return error?.response?.status === 404;
}

function getApiErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

async function assertCurrentFinancialRecordIsEditable(
  freightId,
  { installmentNumber } = {},
) {
  try {
    const result = await freightFinancialApi.getByFreightId(freightId);
    const payload = unwrapApiPayload(result);
    const targetInstallmentNumber = Number(installmentNumber);
    const installments = Array.isArray(payload?.financialInstallments)
      ? payload.financialInstallments
      : [];

    if (
      Number.isFinite(targetInstallmentNumber) &&
      targetInstallmentNumber > 0 &&
      installments.length > 0
    ) {
      const installment = installments.find(
        (item) => Number(item?.installmentNumber) === targetInstallmentNumber,
      );

      if (installment) {
        if (
          !isFinancialRecordEditable(
            installment.statusFinancial ?? installment.financialStatus,
          )
        ) {
          throw new Error(FINANCIAL_EDIT_LOCKED_MESSAGE);
        }
        return;
      }
    }

    if (!isFinancialRecordEditable(getFinancialStatusSource(payload))) {
      throw new Error(FINANCIAL_EDIT_LOCKED_MESSAGE);
    }
  } catch (error) {
    if (isNotFound(error)) return;
    if (error?.message === FINANCIAL_EDIT_LOCKED_MESSAGE) throw error;

    throw new Error(
      getApiErrorMessage(
        error,
        "Não foi possível validar o status do financeiro.",
      ),
    );
  }
}

const freightFinancialService = {
  async getFinancialRecord(record) {
    const freightId = record?.freightId || record?.id;
    const defaults = buildDefaultRecord(record);

    if (!freightId) {
      return defaults;
    }

    try {
      const result = await freightFinancialApi.getByFreightId(freightId);
      const payload = unwrapApiPayload(result);
      const mergedRecord = {
        ...record,
        ...payload,
        financialInstallments: Array.isArray(payload?.financialInstallments)
          ? payload.financialInstallments
          : record?.financialInstallments || [],
      };
      const installmentDefaults = buildDefaultRecord(mergedRecord);

      return normalizeFinancialRecord(payload, {
        ...installmentDefaults,
        freightId,
        installmentNumber: defaults.installmentNumber,
      });
    } catch (error) {
      if (isNotFound(error)) {
        return defaults;
      }

      throw new Error(
        getApiErrorMessage(error, "Não foi possível carregar o financeiro."),
      );
    }
  },

  async saveFinancialRecord(freightId, payload) {
    if (!freightId) {
      throw new Error("Frete não informado para salvar o financeiro.");
    }

    await assertCurrentFinancialRecordIsEditable(freightId, payload);

    try {
      const result = await freightFinancialApi.update(
        freightId,
        buildSavePayload(payload),
      );
      return {
        success: true,
        data: normalizeFinancialRecord(unwrapApiPayload(result), { freightId }),
      };
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Não foi possível salvar o financeiro."),
      );
    }
  },

  async uploadBoleto(freightId, file, options = {}) {
    const validation = validateBoletoFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    if (!freightId) {
      throw new Error("Frete não informado para anexar boleto.");
    }

    const { installmentNumber, financialInstallments } = options;
    await assertCurrentFinancialRecordIsEditable(freightId, {
      installmentNumber,
      financialInstallments,
    });

    try {
      const result = await freightFinancialApi.uploadBoleto(
        freightId,
        file,
        installmentNumber,
      );
      return { success: true, data: unwrapApiPayload(result) };
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Não foi possível anexar o boleto."),
      );
    }
  },

  async removeBoleto(freightId, options = {}) {
    if (!freightId) {
      throw new Error("Frete não informado para remover boleto.");
    }

    const { installmentNumber, financialInstallments } = options;
    await assertCurrentFinancialRecordIsEditable(freightId, {
      installmentNumber,
      financialInstallments,
    });

    try {
      const result = await freightFinancialApi.removeBoleto(
        freightId,
        installmentNumber,
      );
      return { success: true, data: unwrapApiPayload(result) };
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Não foi possível remover o boleto."),
      );
    }
  },

  async sendBoleto(freightId) {
    if (!freightId) {
      throw new Error("Frete não informado para enviar boleto.");
    }

    try {
      const result = await freightFinancialApi.sendBoleto(freightId);
      const payload = unwrapApiPayload(result);
      return {
        success: true,
        message: payload?.message || "Boleto enviado com sucesso.",
        data: payload,
      };
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Não foi possível enviar o boleto."),
      );
    }
  },

  async chargeFreight(freightId) {
    if (!freightId) {
      throw new Error("Frete não informado para efetuar cobrança.");
    }

    try {
      const result = await freightFinancialApi.charge(freightId);
      const payload = unwrapApiPayload(result);
      return {
        success: true,
        message: payload?.message || "Cobrança disparada com sucesso.",
        freightId,
        data: payload,
      };
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Não foi possível efetuar a cobrança."),
      );
    }
  },
};

export default freightFinancialService;
